import React, { useCallback, useEffect, useState } from 'react';
import { usePlaidLink, PlaidLinkOptions, PlaidLinkOnSuccess } from 'react-plaid-link';
import { Button } from './ui';
import { BanknotesIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../api/apiConfig';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';

interface PlaidLinkComponentProps {
  onSuccess: (accounts: any[]) => void;
  onError?: (error: any) => void;
  buttonText?: string;
  buttonVariant?: 'primary' | 'secondary' | 'danger' | 'success' | 'outline' | 'ghost';
  disabled?: boolean;
  /** Pass a Plaid-connected account ID to open Link in update mode (e.g. to add investments consent) */
  updateAccountId?: number;
}

const PlaidLinkComponent: React.FC<PlaidLinkComponentProps> = ({
  onSuccess,
  onError,
  buttonText = 'Connect Bank Account',
  buttonVariant = 'primary',
  disabled = false,
  updateAccountId,
}) => {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch link token from backend
  useEffect(() => {
    const fetchLinkToken = async () => {
      try {
        setLoading(true);
        setError(null);

        const endpoint = updateAccountId
          ? '/api/plaid/create-update-link-token/'
          : '/api/plaid/create-link-token/';
        const body = updateAccountId ? { account_id: updateAccountId } : {};

        const response = await apiClient.post(endpoint, body);

        if (response.data && response.data.link_token) {
          setLinkToken(response.data.link_token);
        } else if (response.data && response.data.setup_required) {
          // Plaid credentials not configured on backend
          setError('Plaid is not configured. Please contact administrator.');
        } else {
          setError('Failed to initialize Plaid connection');
        }
      } catch (err: any) {
        console.error('Error fetching link token:', err);

        if (err.response?.status === 503 && err.response?.data?.setup_required) {
          setError('Plaid credentials not configured. Please add PLAID_CLIENT_ID and PLAID_SECRET to environment variables.');
        } else {
          const errorMessage = err.response?.data?.message || err.message || 'Failed to connect to Plaid';
          setError(errorMessage);
        }

        if (onError) {
          onError(err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchLinkToken();
  }, [onError, updateAccountId]);

  // Handle successful OAuth flow
  const handlePlaidSuccess: PlaidLinkOnSuccess = useCallback(
    async (public_token: string, metadata: any) => {
      try {
        setLoading(true);
        setError(null);

        if (updateAccountId) {
          // Update mode — no token exchange needed. Trigger server-side sync.
          await apiClient.post('/api/plaid/sync-holdings-by-account/', {
            account_id: updateAccountId,
          });
          onSuccess([]);
        } else {
          // Exchange public token for access token and sync accounts
          const response = await apiClient.post('/api/plaid/exchange-token/', {
            public_token
          });

          if (response.data && response.data.success) {
            // Optionally sync transactions immediately
            if (response.data.access_token) {
              try {
                await apiClient.post('/api/plaid/sync-transactions/', {
                  access_token: response.data.access_token
                });
              } catch (syncError) {
                console.warn('Failed to sync transactions immediately:', syncError);
              }
            }
            onSuccess(response.data.accounts || []);
          } else {
            throw new Error('Failed to exchange token');
          }
        }
      } catch (err: any) {
        console.error('Error exchanging public token:', err);
        const errorMessage = err.response?.data?.detail || err.message || 'Failed to connect accounts';
        setError(errorMessage);

        if (onError) {
          onError(err);
        }
      } finally {
        setLoading(false);
      }
    },
    [onSuccess, onError, updateAccountId]
  );

  // Handle errors during OAuth flow
  const handlePlaidExit = useCallback(
    (err: any, metadata: any) => {
      if (err != null) {
        console.error('Plaid Link error:', err);
        setError(err.display_message || err.error_message || 'Connection failed');

        if (onError) {
          onError(err);
        }
      } else {
      }
    },
    [onError]
  );

  // Configure Plaid Link
  const config: PlaidLinkOptions = {
    token: linkToken,
    onSuccess: handlePlaidSuccess,
    onExit: handlePlaidExit,
  };

  const { open, ready } = usePlaidLink(config);

  const handleClick = () => {
    if (error) {
      // If there's an error, try to refetch link token
      window.location.reload();
    } else {
      open();
    }
  };

  // Show error state if Plaid is not configured
  if (error && error.includes('not configured')) {
    return (
      <div
        style={{
          backgroundColor: '#FEF3C7',
          border: '1px solid #FDE68A',
          borderRadius: RADIUS,
          padding: SPACING[16],
        }}
      >
        <h3
          style={{
            fontSize: TYPOGRAPHY.size.sm,
            fontWeight: TYPOGRAPHY.weight.semibold,
            color: '#92400E',
            marginBottom: SPACING[4],
          }}
        >
          Plaid Setup Required
        </h3>
        <p
          style={{
            fontSize: TYPOGRAPHY.size.xs,
            color: '#B45309',
            marginBottom: SPACING[8],
          }}
        >
          {error}
        </p>
        <p
          style={{
            fontSize: TYPOGRAPHY.size.xs,
            color: '#D97706',
          }}
        >
          See{' '}
          <code
            style={{
              backgroundColor: '#FEF9C3',
              padding: `0 ${SPACING[4]}`,
              borderRadius: RADIUS,
            }}
          >
            PLAID_SETUP.md
          </code>{' '}
          for setup instructions.
        </p>
      </div>
    );
  }

  return (
    <>
      {error && !error.includes('not configured') && (
        <div
          style={{
            marginBottom: SPACING[8],
            fontSize: TYPOGRAPHY.size.sm,
            color: COLORS.status.error,
          }}
        >
          {error}
        </div>
      )}

      <Button
        variant={buttonVariant}
        onClick={handleClick}
        disabled={disabled || loading || (!ready && !error)}
        isLoading={loading}
        loadingText="Connecting..."
        leftIcon={<BanknotesIcon style={{ height: '20px', width: '20px' }} />}
      >
        {error && !error.includes('not configured') ? 'Retry Connection' : buttonText}
      </Button>
    </>
  );
};

export default PlaidLinkComponent;
